import React, { useState } from "react";
import PropTypes from "prop-types";
import { toast } from "react-toastify";
import { request } from "../../../config/helpers/axios-instance";
import { getDeliveryTrackingLink } from "../../../components/delivery/helper";
import { partnerName } from "./shared";

// Add or correct the AWB / reference number and tracking link on a manual courier
// delivery after it was booked -- manual vendors often issue the AWB after pickup, so both
// are optional on the Create Delivery form.
//
// Saves through PUT /delivery/admin/manual/:id/tracking, which only accepts manual,
// non-in-house, non-cancelled deliveries. Only CHANGED fields are sent, so correcting the
// link never rewrites the AWB and vice versa.
//
// What the API does on first fill (it decides, not this modal):
//   - AWB added           -> the LSQ AWB activity, which is never sent while it is missing
//   - tracking link added -> the customer's WhatsApp, if it was held back at booking
const isHttpUrl = (value) => /^https?:\/\/\S+$/i.test(value);

function TrackingDetailsModal({ delivery, onClose, onSaved }) {
    const initialAwb = delivery?.deliveryPartnerUniqueId || "";
    const initialLink = getDeliveryTrackingLink(delivery);
    // Set by the API when the customer's WhatsApp was held back at booking for want of a link.
    const messagePending = Boolean(delivery?.additionalDetails?.deliveryMessagePending);

    const [awb, setAwb] = useState(initialAwb);
    const [link, setLink] = useState(initialLink);
    const [saving, setSaving] = useState(false);

    const trimmedAwb = awb.trim();
    const trimmedLink = link.trim();
    const awbChanged = trimmedAwb !== initialAwb;
    const linkChanged = trimmedLink !== initialLink;
    const linkInvalid = trimmedLink !== "" && !isHttpUrl(trimmedLink);

    const save = async () => {
        const body = {};
        if (awbChanged) body.referenceNumber = trimmedAwb;
        if (linkChanged) body.trackingLink = trimmedLink;

        setSaving(true);
        try {
            const result = await request({
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                url: `delivery/admin/manual/${delivery.id}/tracking`,
                data: body,
            });
            if (result.data?.success) {
                toast.success(
                    messagePending && linkChanged && trimmedLink
                        ? "Saved. The customer's delivery WhatsApp has been sent."
                        : "Tracking details saved"
                );
                if (onSaved) onSaved();
                onClose();
                return;
            }
            toast.error(result.data?.err || "Couldn't save tracking details");
        } catch (error) {
            toast.error("Couldn't save tracking details");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="flex min-w-[460px] flex-col gap-[0.9rem]">
            <div className="flex items-center justify-between">
                <span className="text-[1.5rem] font-bold">AWB / Tracking</span>
                <span className="text-[1.2rem]">
                    Delivery ID: <span className="text-[1.2rem] font-semibold">{`#${delivery?.id}`}</span>
                    {" · "}
                    {partnerName(delivery)}
                </span>
            </div>
            <hr />

            <label className="flex flex-col gap-1">
                <span className="text-[1.1rem] font-semibold">Reference / AWB number</span>
                <input
                    type="text"
                    value={awb}
                    onChange={(event) => setAwb(event.target.value)}
                    placeholder="AWB or shipment reference"
                    className="rounded-lg border border-gray-300 px-3 py-2 text-[1.1rem]"
                />
            </label>

            <label className="flex flex-col gap-1">
                <span className="text-[1.1rem] font-semibold">Tracking link</span>
                <input
                    type="url"
                    value={link}
                    onChange={(event) => setLink(event.target.value)}
                    placeholder="https://..."
                    className="rounded-lg border border-gray-300 px-3 py-2 text-[1.1rem]"
                />
                {linkInvalid && (
                    <span className="text-xs text-red-600">Must start with http:// or https://</span>
                )}
            </label>

            {messagePending && (
                <p className="text-sm text-gray-500">
                    The customer&apos;s delivery WhatsApp goes out when the tracking link is first added.
                </p>
            )}

            <div className="flex justify-end gap-3">
                <button
                    type="button"
                    onClick={onClose}
                    disabled={saving}
                    className="rounded-xl border border-gray-300 px-5 py-2 text-[1.2rem] font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                    Close
                </button>
                <button
                    type="button"
                    onClick={save}
                    disabled={saving || linkInvalid || (!awbChanged && !linkChanged)}
                    className="rounded-xl bg-[#00A48B] px-5 py-2 text-[1.2rem] font-semibold text-white-50 hover:bg-[#008f79] disabled:opacity-50"
                >
                    {saving ? "Saving..." : "Save"}
                </button>
            </div>
        </div>
    );
}

TrackingDetailsModal.propTypes = {
    delivery: PropTypes.object.isRequired,
    onClose: PropTypes.func.isRequired,
    onSaved: PropTypes.func,
};

export default TrackingDetailsModal;
